/**
 * HypeFly · 360°-Viewer-Tracking-Hook
 * -----------------------------------
 * Zentraler Hook für Analytics-Events aus den 360°-Viewer-Komponenten
 * (`Video360Viewer`, `Photo360Viewer`, `BeforeAfter360Viewer`).
 *
 * Bewusst als eigenständiges Modul (nicht direkt in den Viewern), damit
 * die WebGL-/HLS-Lade-Architektur der Viewer unangetastet bleibt und
 * Callsites nur wenige Callbacks einhängen.
 *
 * Alle Events landen bei `trackInteraction()` in `analytics.ts`. Auf
 * Admin-Routen ist der Hook automatisch inaktiv (kein Datenlecking).
 */
import { useCallback, useEffect, useRef } from "react";
import { trackInteraction } from "./analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

const INTERACTION_DEBOUNCE_MS = 5000;
const INTERACTION_ANGLE_THRESHOLD_DEG = 30;
const MILESTONES = [25, 50, 75, 100] as const;

/** Erlaubt eine `media_id` in Analytics zu senden? Wir akzeptieren nur echte
 *  UUIDs (`xxxxxxxx-xxxx-...`) und interne Kurz-Slugs (`demo-hero`, `src-*`
 *  wird BEWUSST abgelehnt — das war der alte Fallback-Hash aus dem Titel,
 *  der in Analytics nur Rauschen erzeugt hat). */
function isRealMediaId(id: string | null | undefined): boolean {
    if (!id) return false;
    const t = id.trim();
    if (!t) return false;
    if (t.startsWith("src-")) return false;
    return true;
}

type ViewerType = "video360" | "photo360" | "before_after_360";

type Options = {
    viewerType: ViewerType;
    /** Stabile ID des Mediums (bevorzugt: media_id aus DB). Fallback: hash(src). */
    mediaId?: string | null;
    mediaTitle?: string | null;
    /** Nur setzen, wenn das Medium einem Projekt zugeordnet ist. */
    projectId?: string | null;
    /** Wenn true: kein Tracking (z. B. bei Admin-internen Preview-Renderings). */
    disabled?: boolean;
};

/** Deterministic Hash aus einem String (nur noch für Debug/Log — wird
 *  NICHT mehr als `media_id`-Fallback in Analytics gesendet). */
function _debugHash(s: string): string {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return `dbg-${Math.abs(h).toString(36)}`;
}
void _debugHash;

/** Sendet ein `viewer_*`-Event via Analytics. */
function fire(
    viewerType: ViewerType,
    mediaId: string,
    action: string,
    extra: Record<string, unknown> = {},
) {
    trackInteraction(action, { viewer_type: viewerType, media_id: mediaId, ...extra });
}

export function useViewerTracking(opts: Options): {
    onPlay: (currentTimeSec?: number) => void;
    onPause: (currentTimeSec?: number) => void;
    onComplete: (durationSec?: number) => void;
    onProgress: (currentTimeSec: number, durationSec: number) => void;
    onFullscreen: () => void;
    onInteraction: (yaw?: number, pitch?: number, zoom?: number) => void;
} {
    // Auf Admin-Routen komplett inaktiv.
    const isAdminRoute =
        typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    // Ohne echte DB-Media-ID KEIN Tracking (verhindert `src-*`-Fallback-
    // Rauschen in Analytics). Statische Demo-Assets, Preview-Fallbacks
    // etc. laufen still — sie können in Analytics ohnehin nicht sinnvoll
    // verlinkt/gerendert werden.
    const hasRealId = isRealMediaId(opts.mediaId);
    const disabled = opts.disabled || isAdminRoute || !hasRealId;

    // Wenn wir hier ankommen ist mediaId eine echte UUID → direkt nutzen.
    const resolvedMediaId = (opts.mediaId || "").trim();

    // Refs, die über den Lifecycle des Hooks bestehen bleiben.
    const openedRef = useRef(false);
    const lastInteractionAtRef = useRef(0);
    const lastYawRef = useRef<number | null>(null);
    const milestonesFiredRef = useRef<Set<number>>(new Set());
    const totalWatchedSecondsRef = useRef(0);
    const lastPlayTimestampRef = useRef<number | null>(null);
    // Aktuelle Values in Ref halten, damit sendBeacon-Cleanup den letzten
    // Stand kennt (Closure-Probleme vermeiden).
    const stateRef = useRef({ viewerType: opts.viewerType, mediaId: resolvedMediaId });
    stateRef.current = { viewerType: opts.viewerType, mediaId: resolvedMediaId };

    // ---- Mount: viewer_open + pagehide-Listener ----
    useEffect(() => {
        if (disabled) return;
        if (openedRef.current) return;
        openedRef.current = true;

        fire(opts.viewerType, resolvedMediaId, "viewer_open", {
            media_title: opts.mediaTitle || null,
            project_id: opts.projectId || null,
            source_page: window.location.pathname,
        });

        // Wenn User Play gedrückt hatte, aber die Seite verlässt bevor
        // onPause/onComplete triggern: totale Watch-Zeit via sendBeacon.
        const flushOnLeave = () => {
            // Falls gerade noch abgespielt wird, letzte Sekunden anrechnen.
            if (lastPlayTimestampRef.current !== null) {
                totalWatchedSecondsRef.current +=
                    (Date.now() - lastPlayTimestampRef.current) / 1000;
                lastPlayTimestampRef.current = null;
            }
            const total = Math.max(0, Math.round(totalWatchedSecondsRef.current));
            if (total < 1) return; // < 1s ist unter dem Signal-Rauschen
            const payload = {
                session_id: sessionStorage.getItem("hf_session_id") || "",
                event_type: "interaction",
                page_path: window.location.pathname,
                vt_code: sessionStorage.getItem("hf_vt_code"),
                custom_data: {
                    action: "viewer_close",
                    viewer_type: stateRef.current.viewerType,
                    media_id: stateRef.current.mediaId,
                    total_watched_seconds: total,
                },
            };
            try {
                if (navigator.sendBeacon) {
                    const blob = new Blob([JSON.stringify(payload)], {
                        type: "application/json",
                    });
                    navigator.sendBeacon(`${BACKEND_URL}/api/track`, blob);
                }
            } catch {
                /* silent */
            }
        };
        window.addEventListener("pagehide", flushOnLeave, { capture: true });
        window.addEventListener("beforeunload", flushOnLeave, { capture: true });
        return () => {
            window.removeEventListener("pagehide", flushOnLeave, { capture: true });
            window.removeEventListener("beforeunload", flushOnLeave, { capture: true });
            // Bei Component-Unmount auch flush (z. B. Modal geschlossen).
            flushOnLeave();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, resolvedMediaId]);

    // ---- Callbacks ----
    const onPlay = useCallback(
        (currentTimeSec = 0) => {
            if (disabled) return;
            lastPlayTimestampRef.current = Date.now();
            fire(opts.viewerType, resolvedMediaId, "viewer_play", {
                at_seconds: Math.round(currentTimeSec),
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [disabled, resolvedMediaId],
    );

    const onPause = useCallback(
        (currentTimeSec = 0) => {
            if (disabled) return;
            if (lastPlayTimestampRef.current !== null) {
                totalWatchedSecondsRef.current +=
                    (Date.now() - lastPlayTimestampRef.current) / 1000;
                lastPlayTimestampRef.current = null;
            }
            fire(opts.viewerType, resolvedMediaId, "viewer_pause", {
                at_seconds: Math.round(currentTimeSec),
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [disabled, resolvedMediaId],
    );

    const onComplete = useCallback(
        (durationSec = 0) => {
            if (disabled) return;
            if (lastPlayTimestampRef.current !== null) {
                totalWatchedSecondsRef.current +=
                    (Date.now() - lastPlayTimestampRef.current) / 1000;
                lastPlayTimestampRef.current = null;
            }
            fire(opts.viewerType, resolvedMediaId, "viewer_complete", {
                duration_seconds: Math.round(durationSec),
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [disabled, resolvedMediaId],
    );

    const onProgress = useCallback(
        (currentTimeSec: number, durationSec: number) => {
            if (disabled || !durationSec || durationSec <= 0) return;
            const pct = Math.floor((currentTimeSec / durationSec) * 100);
            for (const m of MILESTONES) {
                if (pct >= m && !milestonesFiredRef.current.has(m)) {
                    milestonesFiredRef.current.add(m);
                    fire(opts.viewerType, resolvedMediaId, "viewer_watch_milestone", {
                        milestone: m,
                    });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [disabled, resolvedMediaId],
    );

    const onFullscreen = useCallback(() => {
        if (disabled) return;
        fire(opts.viewerType, resolvedMediaId, "viewer_fullscreen");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, resolvedMediaId]);

    const onInteraction = useCallback(
        (yaw?: number, pitch?: number, zoom?: number) => {
            if (disabled) return;
            const now = Date.now();
            const sinceLast = now - lastInteractionAtRef.current;
            const yawDelta =
                typeof yaw === "number" && lastYawRef.current !== null
                    ? Math.abs(yaw - lastYawRef.current)
                    : Infinity;
            // Sende, wenn ≥ 5s vergangen sind ODER Winkel-Delta > 30°.
            if (
                sinceLast < INTERACTION_DEBOUNCE_MS &&
                yawDelta < INTERACTION_ANGLE_THRESHOLD_DEG
            ) {
                return;
            }
            lastInteractionAtRef.current = now;
            if (typeof yaw === "number") lastYawRef.current = yaw;
            fire(opts.viewerType, resolvedMediaId, "viewer_interaction", {
                yaw: typeof yaw === "number" ? Math.round(yaw) : undefined,
                pitch: typeof pitch === "number" ? Math.round(pitch) : undefined,
                zoom: typeof zoom === "number" ? Math.round(zoom) : undefined,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [disabled, resolvedMediaId],
    );

    return { onPlay, onPause, onComplete, onProgress, onFullscreen, onInteraction };
}
